import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { settingsApi } from '@/api/settings'
import { useTheme } from '@/composables/useTheme'

/**
 * Settings Store - Manages all CMS content dynamically
 * Loads settings from the CMS and provides them across the app
 */
export const useSettingsStore = defineStore('settings', () => {
  // State
  const settings = ref({})
  const pages = ref([])
  const theme = ref({
    light: {},
    dark: {},
    global: {},
  })
  const loading = ref(false)
  const error = ref(null)
  const loaded = ref(false)
  const themeLoaded = ref(false)
  const homepageLoaded = ref(false)

  // Theme composable
  const { setThemeData, initTheme } = useTheme()

  // Getters
  const getSetting = computed(() => (group, key, defaultValue = '') => {
    return settings.value[group]?.[key] ?? defaultValue
  })

  const getGroup = computed(() => (group) => {
    return settings.value[group] ?? {}
  })

  const generalSettings = computed(() => getGroup.value('general'))
  const contactSettings = computed(() => getGroup.value('contact'))
  const socialSettings = computed(() => getGroup.value('social'))
  const seoSettings = computed(() => getGroup.value('seo'))
  const footerSettings = computed(() => getGroup.value('footer'))
  const headerSettings = computed(() => getGroup.value('header'))
  const featuresSettings = computed(() => getGroup.value('features'))
  const contactPageSettings = computed(() => getGroup.value('contact_page'))
  const serviceCenterPageSettings = computed(() => getGroup.value('service_center_page'))
  const distributorsPageSettings = computed(() => getGroup.value('distributors_page'))
  const supportPageSettings = computed(() => getGroup.value('support_page'))

  const seoKeywords = computed(() => {
    const seo = seoSettings.value
    if (seo?.meta_keywords) {
      if (typeof seo.meta_keywords === 'string') {
        return seo.meta_keywords.split(',').map(k => k.trim()).filter(Boolean)
      }
      return seo.meta_keywords
    }
    return []
  })

  const seoTitle = computed(() => seoSettings.value?.meta_title || 'Inversun - Premium Solar Energy Solutions in Lebanon')
  const seoDescription = computed(() => seoSettings.value?.meta_description || 'Leading provider of solar pump inverters, VFD inverters, reactors, and sinewave filters in Lebanon.')

  // Actions
  async function fetchAllSettings() {
    if (loaded.value) return

    loading.value = true
    error.value = null

    const defaults = getDefaultSettings()
    const currentSettings = settings.value || {}

    settings.value = Object.keys(defaults).reduce((mergedSettings, group) => {
      const defaultGroup = defaults[group]
      const currentGroup = currentSettings[group]

      if (
        defaultGroup &&
        typeof defaultGroup === 'object' &&
        !Array.isArray(defaultGroup) &&
        currentGroup &&
        typeof currentGroup === 'object' &&
        !Array.isArray(currentGroup)
      ) {
        mergedSettings[group] = {
          ...defaultGroup,
          ...currentGroup,
        }
        return mergedSettings
      }

      mergedSettings[group] = currentGroup ?? defaultGroup
      return mergedSettings
    }, { ...currentSettings })

    loaded.value = true
    loading.value = false
  }

  async function fetchHomepageData() {
    if (homepageLoaded.value) return settings.value.homepage || {}

    try {
      const response = await settingsApi.getHomepageData()
      const homepageData = response?.data || {}

      settings.value = {
        ...settings.value,
        homepage: {
          ...(settings.value.homepage || {}),
          ...homepageData,
        },
      }

      homepageLoaded.value = true
      return settings.value.homepage
    } catch (e) {
      console.error('Failed to fetch homepage data:', e)
      return settings.value.homepage || {}
    }
  }

  async function fetchPages() {
    try {
      const response = await settingsApi.getPages()
      pages.value = response.data || []
    } catch (e) {
      console.error('Failed to fetch pages:', e)
      pages.value = []
    }
  }

  async function fetchTheme() {
    if (themeLoaded.value) return

    theme.value = getDefaultTheme()
    themeLoaded.value = true

    // Apply the local theme defaults without a CMS round-trip.
    setThemeData(theme.value)
    initTheme()
  }

  function getDefaultTheme() {
    return {
      light: {
        '--theme-global-primary-color': '#3B82F6',
        '--theme-global-secondary-color': '#F59E0B',
        '--theme-hero-background-color': '#030712',
        '--theme-hero-text-color': '#FFFFFF',
        '--theme-about-background-color': '#FFFFFF',
        '--theme-vision-background-color': '#0A0F1C',
        '--theme-features-background-color': '#F9FAFB',
        '--theme-features-card-background': '#FFFFFF',
        '--theme-contact-background-color': '#FFFFFF',
        '--theme-contact-form-background': '#0A0F1C',
        '--theme-cta-background-color': '#030712',
      },
      dark: {
        '--theme-global-primary-color': '#60A5FA',
        '--theme-global-secondary-color': '#FBBF24',
        '--theme-hero-background-color': '#030712',
        '--theme-hero-text-color': '#FFFFFF',
        '--theme-about-background-color': '#0A0F1C',
        '--theme-vision-background-color': '#111827',
        '--theme-features-background-color': '#111827',
        '--theme-features-card-background': '#1F2937',
        '--theme-contact-background-color': '#0A0F1C',
        '--theme-contact-form-background': '#111827',
        '--theme-cta-background-color': '#030712',
      },
      global: {
        heading_font: 'Inter',
        body_font: 'Inter',
        border_radius: 20,
        enable_dark_mode: true,
      },
    }
  }

  function getDefaultSettings() {
    return {
      general: {
        site_name: 'Inversun',
        site_tagline: 'Premium Solar Energy Solutions',
        site_description: 'Leading provider of solar panels, inverters, and energy storage solutions in Lebanon.',
      },
      header: {
        nav_home: 'Home',
        nav_shop: 'Shop',
        nav_products: 'Products',
        nav_about: 'About',
        nav_services: 'Service Center',
        nav_distributors: 'Distributors',
        nav_support: 'Support',
        nav_contact: 'Contact',
      },
      contact: {
        phone: '+961 76 984 611',
        email: 'sales@inversun.tech',
        whatsapp: '+96176984611',
        address: 'Zahle-Baalbeck Highway, Haouch Snaid-Talya, Bekaa Lebanon',
        business_hours: 'Mon-Sat: 9AM - 6PM',
      },
      social: {
        show_facebook: true,
        show_instagram: true,
        show_linkedin: true,
        show_tiktok: true,
        facebook: 'https://facebook.com/Inversun',
        instagram: 'https://instagram.com/inversun.lb',
        linkedin: 'https://linkedin.com/company/inversun',
        tiktok: 'https://tiktok.com/@inversun.lb',
      },
      footer: {
        tagline: 'Premium solar energy solutions for a sustainable future.',
        show_privacy_policy: false,
        show_terms: false,
      },
      homepage: {
        hero_title_line1: 'Harvest the Sun',
        hero_title_line2: 'Power What Matters',
        hero_subtitle: 'Premium solar pump inverters, reactors, sinewave filters, and control accessories built for demanding installations in Lebanon and beyond.',
        hero_cta_text: 'Explore Products',
        hero_cta_link: '/products',
        hero_gallery_image_1: '/images/service-center.jpg',
        about_badge: 'About Inversun',
        about_title: 'Reliable solar and motor-control solutions, backed locally.',
        about_paragraph1: 'Inversun supplies high-performance solar pump inverters, VFD systems, reactors, and sinewave filters for installers, integrators, and industrial projects.',
        about_paragraph2: 'We focus on dependable hardware, practical technical guidance, and after-sales support that helps customers deploy systems with confidence.',
        about_image1: '/images/service-center.jpg',
        about_image2: '/images/service-center.jpg',
        vision_badge: 'Vision & Mission',
        vision_title: 'Engineering-focused products with real on-ground support.',
        vision_paragraph1: 'Our mission is to make efficient energy and pump-control equipment easier to source, specify, and maintain for the Lebanese market.',
        vision_paragraph2: 'We combine carefully selected product lines with responsive service, documentation, and technical follow-up for installers and end users.',
        stat1_number: '1000+',
        stat1_label: 'Systems Supported',
        stat2_number: '24/7',
        stat2_label: 'Technical Guidance',
        stat3_number: '10+',
        stat3_label: 'Product Lines',
        vision_image: '/images/service-center.jpg',
        features_badge: 'Why Inversun',
        features_title: 'Built around reliability, performance, and support.',
        features_subtitle: 'A focused portfolio of proven products for solar pumping and industrial motor applications.',
        feature1_title: 'Quality Equipment',
        feature1_item1: 'Carefully selected inverter and protection components',
        feature1_item2: 'Stable performance in demanding field conditions',
        feature1_item3: 'Models suited for residential, agricultural, and industrial use',
        feature2_title: 'Practical Value',
        feature2_item1: 'Efficient solutions sized for real project requirements',
        feature2_item2: 'Competitive options across core product categories',
        feature2_item3: 'Inventory aligned with common market needs',
        feature3_title: 'Customer Service',
        feature3_item1: 'Fast responses for product and sizing questions',
        feature3_item2: 'Guidance before and after purchase',
        feature3_item3: 'Support tailored to installers and project teams',
        feature4_title: 'After-Sales Confidence',
        feature4_item1: 'Responsive troubleshooting assistance',
        feature4_item2: 'Documentation and setup guidance where needed',
        feature4_item3: 'Service-oriented local presence',
        cta_badge: 'Start Your Project',
        cta_title_line1: 'Need the right equipment for your next',
        cta_title_highlight: 'solar pumping or control project?',
        cta_description: 'Talk to Inversun for product recommendations, pricing, and technical guidance.',
        cta_button1_text: 'Call Us',
        cta_button1_link: 'tel:+96176984611',
        cta_button2_text: 'Browse Products',
        cta_button2_link: '/products',
      },
      products: {
        page_title: 'Product Categories',
        page_subtitle: 'Browse solar pump inverters, reactors, sinewave filters, and accessories selected for reliable field performance.',
      },
      seo: {
        meta_title: 'Inversun - Premium Solar Energy Solutions in Lebanon',
        meta_description: 'High-quality solar panels, inverters, and battery storage systems.',
        meta_keywords: 'solar pump inverter, inversun pump inverter, VFD pump inverter, invt pump inverter, usfull pump inverter, veichi pump inverter, inversun solar pump inverter, reactor, reactor 3%, reactor 5%, inversun reactor, invt reactor, sinewave filter, inversun sinewave filter, invt sinewave filter, viechi sinewave filter',
        og_image: '/images/service-center.jpg',
      },
      contact_page: {},
      service_center_page: {},
      distributors_page: {},
      support_page: {},
    }
  }

  // Initialize - load all settings
  async function init() {
    await Promise.all([
      fetchAllSettings(),
      fetchTheme(),
    ])
  }

  return {
    // State
    settings,
    pages,
    theme,
    loading,
    error,
    loaded,
    themeLoaded,
    homepageLoaded,

    // Getters
    getSetting,
    getGroup,
    generalSettings,
    contactSettings,
    socialSettings,
    seoSettings,
    footerSettings,
    headerSettings,
    featuresSettings,
    contactPageSettings,
    serviceCenterPageSettings,
    distributorsPageSettings,
    supportPageSettings,
    seoKeywords,
    seoTitle,
    seoDescription,

    // Actions
    fetchAllSettings,
    fetchHomepageData,
    fetchPages,
    fetchTheme,
    init,
    getDefaultSettings,
    getDefaultTheme,
  }
})
